import { getStoredAccessToken } from '@/lib/storage';
import { isTokenStorageAuthClient } from '@/lib/session-health';

function buildWsUrl(baseUrl: string, ticket?: string | null): string {
  const url = baseUrl.replace(/^http/, 'ws').replace(/\/$/, '');
  const wsBase = url.endsWith('/ws') ? url : `${url}/ws`;
  if (ticket) {
    return `${wsBase}?ticket=${encodeURIComponent(ticket)}`;
  }
  return wsBase;
}

function isWebCookieClient(): boolean {
  return !isTokenStorageAuthClient();
}

export interface WSClientOptions {
  url: string;
  token?: string;
  ticket?: string;
  onConnect?: () => void;
  onDisconnect?: () => void;
}

export class WSClient {
  private ws: WebSocket | null = null;
  private wsUrl: string;
  private baseUrl: string;
  private token: string;
  private ticket: string | null = null;
  private onConnectCb?: () => void;
  private onDisconnectCb?: () => void;
  private reconnectAttempts = 0;
  private reconnectDelay = 1000;
  private maxReconnectDelay = 60000;
  private shouldReconnect = false;
  private listeners: Map<string, Set<(data: any) => void>> = new Map();
  private connectionState: 'connecting' | 'connected' | 'disconnected' = 'disconnected';
  /** T-509: после 10 неудачных попыток — баннер «Соединение потеряно» */
  private static readonly MAX_RECONNECT_ATTEMPTS_BEFORE_BANNER = 10;
  onConnectionLost?: () => void;
  onConnectionRestored?: () => void;

  constructor(options: WSClientOptions | string) {
    if (typeof options === 'string') {
      this.baseUrl = options;
      this.wsUrl = buildWsUrl(options);
      this.token = '';
      return;
    }
    this.baseUrl = options.url;
    this.wsUrl = buildWsUrl(options.url, options.ticket);
    this.token = options.token || '';
    this.ticket = options.ticket || null;
    this.onConnectCb = options.onConnect;
    this.onDisconnectCb = options.onDisconnect;
  }

  /**
   * Подключение с поддержкой трёх методов аутентификации:
   * 1. Web с ticket: запрашиваем ticket, передаём в URL
   * 2. Native с token: используем Bearer token из хранилища
   * 3. Fallback: cookie-only (для web если ticket недоступен)
   */
  async connect(tokenOrTicket?: string) {
    if (typeof window === 'undefined') return;

    // Если уже подключены или подключаемся — не дублируем
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }

    this.shouldReconnect = true;
    this.connectionState = 'connecting';

    const isWeb = isWebCookieClient();
    const storedToken = getStoredAccessToken();
    let effectiveToken = tokenOrTicket || this.token || storedToken || '';

    // Для web-клиентов предпочитаем ticket вместо access_token
    if (isWeb) {
      try {
        const { api } = await import('@/lib/api');
        const ticketResponse = await api.issueWsTicket();
        if (ticketResponse?.ticket) {
          this.ticket = ticketResponse.ticket;
          effectiveToken = ''; // Не используем token если есть ticket
        }
      } catch (err) {
        console.warn('[WS] Failed to get ticket, falling back to cookie-only:', err);
        this.ticket = null;
        effectiveToken = '';
      }
    }

    // Обновляем URL с ticket если получили
    const wsUrl = buildWsUrl(this.baseUrl, this.ticket);

    try {
      this.ws = new WebSocket(wsUrl);
    } catch (err) {
      console.error('[WS] Failed to create WebSocket:', err);
      this.handleDisconnect();
      return;
    }

    this.ws.onopen = () => {
      console.log('[WS] Connected');
      this.connectionState = 'connected';
      const wasBeyondBanner = this.reconnectAttempts >= WSClient.MAX_RECONNECT_ATTEMPTS_BEFORE_BANNER;
      this.reconnectAttempts = 0;
      if (wasBeyondBanner) {
        this.onConnectionRestored?.();
      }

      // Для native-режима или если нет ticket — отправляем auth фрейм
      if (!isWeb && effectiveToken) {
        try {
          this.ws?.send(JSON.stringify({ type: 'auth', access_token: effectiveToken }));
        } catch {
          /* ignore */
        }
      }
      // Для web с ticket — сервер авторизует по URL, дополнительный фрейм не нужен
      // Но отправим пинг для подтверждения
      if (isWeb && this.ticket) {
        try {
          this.ws?.send(JSON.stringify({ type: 'auth', ticket: this.ticket }));
        } catch {
          /* ignore */
        }
      }

      this.onConnectCb?.();
    };

    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        const eventType = data.event ?? data.type;
        if (!eventType) return;

        // Обработка ошибок авторизации
        if (eventType === 'error' && data.data?.code === 4001) {
          console.error('[WS] Auth error:', data.data?.message);
          this.handleAuthError();
          return;
        }

        const listeners = this.listeners.get(eventType);
        if (listeners) {
          const payload = data.data && typeof data.data === 'object'
            ? {
                ...data.data,
                ...(data.room_id !== undefined && data.data.room_id === undefined ? { room_id: data.room_id } : {}),
              }
            : (data.data ?? data);
          listeners.forEach((callback) => callback(payload));
        }
      } catch (error) {
        console.error('[WS] Parse error:', error);
      }
    };

    this.ws.onerror = (error) => {
      console.error('[WS] Error:', error);
      // Не вызываем disconnect здесь — onclose придёт после
    };

    this.ws.onclose = (event) => {
      console.log('[WS] Disconnected', event.code, event.reason);
      this.connectionState = 'disconnected';
      this.onDisconnectCb?.();

      // Очистка старого ticket при ошибке авторизации
      if (event.code === 4001) {
        this.ticket = null;
      }

      if (this.shouldReconnect) {
        this.attemptReconnect();
      }
    };
  }

  private handleAuthError() {
    // Сбрасываем ticket и пробуем переподключиться
    this.ticket = null;
    this.disconnect();
    setTimeout(() => this.connect(), 1000);
  }

  private handleDisconnect() {
    this.connectionState = 'disconnected';
    this.onDisconnectCb?.();
    if (this.shouldReconnect) {
      this.attemptReconnect();
    }
  }

  private attemptReconnect() {
    if (!this.shouldReconnect) {
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
      this.maxReconnectDelay
    );

    console.log(`[WS] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})...`);
    if (this.reconnectAttempts === WSClient.MAX_RECONNECT_ATTEMPTS_BEFORE_BANNER) {
      this.onConnectionLost?.();
    }

    setTimeout(() => {
      void (async () => {
        if (!this.shouldReconnect) return;

        // Перед reconnect обновляем сессию
        try {
          const { api } = await import('@/lib/api');
          const fresh = await api.ensureSession(false);
          if (fresh && fresh !== '__cookie_session__') {
            this.token = fresh;
          }
        } catch {
          /* ignore */
        }

        // Сбрасываем ticket для получения нового
        const hadTicket = this.ticket;
        this.ticket = null;

        // Пробуем подключиться
        await this.connect(this.token);

        // Если не подключились и был ticket — пробуем без него (cookie-only)
        if (!this.isConnected && hadTicket) {
          console.log('[WS] Retrying with cookie-only...');
          await this.connect('');
        }
      })();
    }, delay);
  }

  reconnect(token?: string) {
    if (token) {
      this.token = token;
    }
    this.shouldReconnect = true;
    this.reconnectAttempts = 0;
    this.ticket = null; // Получим новый ticket при reconnect

    // Закрываем старое соединение
    if (this.ws) {
      const ws = this.ws;
      this.ws = null;
      ws.onopen = null;
      ws.onmessage = null;
      ws.onerror = null;
      ws.onclose = null;
      try {
        ws.close();
      } catch {
        // Ignore close failures
      }
    }

    // Подключаемся заново
    void this.connect(this.token);
  }

  disconnect() {
    this.shouldReconnect = false;
    if (this.ws) {
      try {
        this.ws.close();
      } catch {
        // Ignore
      }
      this.ws = null;
    }
    this.connectionState = 'disconnected';
    this.ticket = null;
  }

  get readyState() {
    return this.ws?.readyState ?? WebSocket.CLOSED;
  }

  get isConnected() {
    return this.connectionState === 'connected' && this.ws?.readyState === WebSocket.OPEN;
  }

  get isConnecting() {
    return this.connectionState === 'connecting' || this.ws?.readyState === WebSocket.CONNECTING;
  }

  /** API expects { event, data, room_id } */
  send(event: string, data: any = {}, roomId?: number) {
    if (this.ws?.readyState !== WebSocket.OPEN) return false;
    const payload: Record<string, unknown> = {
      event,
      data,
      ...(roomId !== undefined && { room_id: roomId }),
    };
    try {
      this.ws.send(JSON.stringify(payload));
      return true;
    } catch {
      return false;
    }
  }

  on(type: string, callback: (data: any) => void) {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, new Set());
    }
    this.listeners.get(type)!.add(callback);

    return () => {
      this.off(type, callback);
    };
  }

  off(type: string, callback: (data: any) => void) {
    const listeners = this.listeners.get(type);
    if (listeners) {
      listeners.delete(callback);
    }
  }
}
